export interface Vector2D {
	x: number;
	y: number;
}

/**
 * Creates a 2D vector object.
 * @param x The x-coordinate of the vector.
 * @param y The y-coordinate of the vector.
 * @returns A 2D vector object.
 */
export function createVector2D(
	x: number,
	y: number,
): Vector2D {
	return {
		x,
		y,
	};
}

/**
 * Subtracts two 2D vectors between each other's coordinates.
 * @param vectorA The first vector.
 * @param vectorB The second vector.
 * @returns The resulting vector after subtraction.
 */
export function subtractVectors(
	vectorA: Vector2D,
	vectorB: Vector2D,
): Vector2D {
	return createVector2D(
		vectorA.x - vectorB.x,
		vectorA.y - vectorB.y,
	);
}

/**
 * Adds two 2D vectors together by summing their coordinates.
 * @param vectorA The first vector.
 * @param vectorB The second vector.
 * @returns The resulting vector after addition.
 */
export function addVectors(
	vectorA: Vector2D,
	vectorB: Vector2D,
): Vector2D {
	return createVector2D(
		vectorA.x + vectorB.x,
		vectorA.y + vectorB.y,
	);
}

/**
 * Scales a 2D vector by a scalar factor value.
 * @param vector The vector to scale.
 * @param scalar The scalar factor value to scale the vector by.
 * @returns The resulting scaled vector.
 */
export function scaleVector(
	vector: Vector2D,
	scalar: number,
): Vector2D {
	return createVector2D(
		vector.x * scalar,
		vector.y * scalar,
	);
}

/**
 * Calculates the dot product of two 2D vectors.
 * The dot product is the sum of the products of the corresponding coordinates.
 * It is a measure of how much one vector extends in the direction of another.
 * @param vectorA The first vector.
 * @param vectorB The second vector.
 * @returns The dot product of the two vectors.
 */
export function dotProduct(
	vectorA: Vector2D,
	vectorB: Vector2D,
): number {
	return vectorA.x * vectorB.x + vectorA.y * vectorB.y;
}

/**
 * Calculates the length (magnitude) of a 2D vector.
 * The length is the square root of the sum of the squares of its coordinates.
 * It represents the distance from the origin to the point represented by the vector.
 * @param vector The vector to calculate the length of.
 * @returns The length of the vector.
 */
export function vectorLength(vector: Vector2D): number {
	return Math.hypot(
		vector.x,
		vector.y,
	);
}

/**
 * Calculates the unit vector of a 2D vector.
 * A unit vector is a vector with a length of 1 that points in the same direction
 * as the original vector. It is obtained by dividing each coordinate of the vector
 * by the vector's length.
 * @example
 * const vector = { x: 3, y: 4 };
 * const unit = unitVector(vector); // { x: 0.6, y: 0.8 }
 * @remarks
 * If the vector is a zero vector (length 0), this function will return a vector
 * with coordinates (0, 0) to avoid division by zero.
 * This is a common practice in vector mathematics to handle such cases gracefully.
 * @see {@link vectorLength} for calculating the length of a vector.
 * @see {@link scaleVector} for scaling a vector by a scalar.
 * @see {@link createVector2D} for creating a vector object.
 * @see {@link Vector2D} for the vector type definition.
 * @param vector The vector to calculate the unit vector for.
 * @returns The unit vector in the same direction as the original vector.
 */
export function unitVector(vector: Vector2D): Vector2D {
	return scaleVector(
		vector,
		1 / (vectorLength(vector) || 1),
	);
}
